import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { DetailList, DetailRow } from '@/shared/ui/detail-list';
import { Mono } from '@/shared/ui/mono';
import { StatusDot, type StatusTone } from '@/shared/ui/status-dot';

import type { CheckState, LiveState, ReadinessState } from '../api/status';
import { useApiStatus } from '../hooks/use-api-status';

const LIVE_TONE: Record<LiveState, StatusTone> = { up: 'success', down: 'destructive' };
const READY_TONE: Record<ReadinessState, StatusTone> = {
  ready: 'success',
  not_ready: 'warning',
  unreachable: 'destructive',
};
const CHECK_TONE: Record<CheckState, StatusTone> = {
  ok: 'success',
  failed: 'destructive',
  unknown: 'muted',
};

/** Console home side panel: what the API says about itself plus this build's identity. */
export function SystemPanel() {
  const { t } = useTranslation(['system', 'common']);
  const format = useFormat();
  const { data, isPending, isFetching, refetch } = useApiStatus();
  const { env, release, apiBaseUrl } = getRuntimeConfig();

  return (
    <Card className="gap-4 py-5">
      <CardHeader className="px-5">
        <CardTitle className="text-[15px]">{t('system:panel.title')}</CardTitle>
        <CardDescription>
          {data
            ? t('system:panel.checkedAt', { time: format.time(data.checkedAt) })
            : t('system:line.checking')}
        </CardDescription>
        <CardAction>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label={t('system:panel.refresh')}
          >
            <RefreshCw className={isFetching ? 'animate-spin' : undefined} aria-hidden />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="px-5">
        {isPending || !data ? (
          <div className="space-y-3" aria-busy>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
          </div>
        ) : (
          <DetailList>
            <DetailRow label={t('system:panel.api')}>
              <StatusDot tone={LIVE_TONE[data.live]}>{t(`system:live.${data.live}`)}</StatusDot>
            </DetailRow>
            <DetailRow label={t('system:panel.readiness')}>
              <StatusDot tone={READY_TONE[data.readiness]}>
                {t(`system:readiness.${data.readiness}`)}
              </StatusDot>
            </DetailRow>
            <DetailRow label={t('system:checks.postgres')}>
              <StatusDot tone={CHECK_TONE[data.checks.postgres]}>
                {t(`system:check.${data.checks.postgres}`)}
              </StatusDot>
            </DetailRow>
            <DetailRow label={t('system:checks.redis')}>
              <StatusDot tone={CHECK_TONE[data.checks.redis]}>
                {t(`system:check.${data.checks.redis}`)}
              </StatusDot>
            </DetailRow>
            <DetailRow label={t('system:panel.version')}>
              <span className="text-muted-foreground">{t('system:panel.versionUnknown')}</span>
            </DetailRow>
            <DetailRow label={t('system:panel.console')}>
              <Mono className="text-foreground">{release}</Mono>
            </DetailRow>
            <DetailRow label={t('system:panel.environment')}>{t(`common:env.${env}`)}</DetailRow>
            <DetailRow label={t('system:panel.endpoint')}>
              <Mono>{apiBaseUrl}</Mono>
            </DetailRow>
          </DetailList>
        )}
      </CardContent>
    </Card>
  );
}
